import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TabNavigation from '../components/TabNavigation';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { Leaf, Factory, Plus, Calendar, MapPin, TrendingUp, Target, BarChart3, Globe, Recycle, Shield, Award, Building2, Database } from 'lucide-react';
import CBAMModule from './sustainability/CBAMModule';
import SSuMModule from './sustainability/SSuMModule';
import GRICSRDModule from './sustainability/GRICSRDModule';
import GRIv2Module from './sustainability/GRIv2Module';
import LCAModule from './sustainability/LCAModule';
import BRSRModule from './sustainability/BRSRModule';
import SGXModule from './sustainability/SGXModule';
import EcoVadisModule from './sustainability/EcoVadisModule';
import ReportMetadata from './sustainability/ReportMetadata';

const API = process.env.REACT_APP_BACKEND_URL;

const SustainabilityHub = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { isModuleEnabled, loading: moduleLoading } = useModuleAccess();
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [activeModule, setActiveModule] = useState('ssum'); // 'cbam', 'ssum', 'gri', 'gri-v2', 'lca', 'brsr', 'sgx', 'ecovadis', or 'metadata'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStudies();
  }, []);

  const fetchStudies = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ers/studies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudies(response.data);
    } catch (error) {
      console.error('Error fetching studies:', error);
    } finally {
      setLoading(false);
    }
  };

  if (selectedStudy) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back button and Module Switcher */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setSelectedStudy(null)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <span>←</span> Back to Studies
            </button>
            
            <div className="flex gap-2 flex-wrap">
              {isModuleEnabled('ssum') && (
                <button
                  onClick={() => setActiveModule('ssum')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    activeModule === 'ssum'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Target className="w-4 h-4" />
                  SSuM
                </button>
              )}
              {isModuleEnabled('cbam') && (
                <button
                  onClick={() => setActiveModule('cbam')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    activeModule === 'cbam'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Leaf className="w-4 h-4" />
                  CBAM
                </button>
              )}
              {isModuleEnabled('gri') && (
                <button
                  onClick={() => setActiveModule('gri')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    activeModule === 'gri'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  GRI v1
                </button>
              )}
              {isModuleEnabled('gri') && (
                <button
                  onClick={() => setActiveModule('gri-v2')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    activeModule === 'gri-v2'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  GRI v2
                </button>
              )}
              {isModuleEnabled('lca') && (
                <button
                  onClick={() => setActiveModule('lca')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    activeModule === 'lca'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Recycle className="w-4 h-4" />
                  LCA
                </button>
              )}
              {isModuleEnabled('brsr') && (
                <button
                  onClick={() => setActiveModule('brsr')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    activeModule === 'brsr'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  BRSR
                </button>
              )}
              {isModuleEnabled('sgx') && (
                <button
                  onClick={() => setActiveModule('sgx')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    activeModule === 'sgx'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  SGX
                </button>
              )}
              {isModuleEnabled('ecovadis') && (
                <button
                  onClick={() => setActiveModule('ecovadis')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    activeModule === 'ecovadis'
                      ? 'bg-green-700 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Award className="w-4 h-4" />
                  EcoVadis
                </button>
              )}
              <button
                onClick={() => setActiveModule('metadata')}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                  activeModule === 'metadata'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Database className="w-4 h-4" />
                Report Data
              </button>
            </div>
          </div>

          {/* Render Selected Module */}
          {activeModule === 'cbam' && <CBAMModule study={selectedStudy} />}
          {activeModule === 'ssum' && <SSuMModule study={selectedStudy} />}
          {activeModule === 'gri' && <GRICSRDModule study={selectedStudy} />}
          {activeModule === 'gri-v2' && <GRIv2Module study={selectedStudy} />}
          {activeModule === 'lca' && <LCAModule study={selectedStudy} />}
          {activeModule === 'brsr' && <BRSRModule study={selectedStudy} />}
          {activeModule === 'sgx' && <SGXModule study={selectedStudy} />}
          {activeModule === 'ecovadis' && <EcoVadisModule study={selectedStudy} />}
          {activeModule === 'metadata' && <ReportMetadata study={selectedStudy} />}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Sustainability Management</h1>
          <p className="text-gray-600 mt-2">Strategic Maturity (SSuM) • EU Carbon (CBAM) • Global Reporting (GRI/CSRD) • Life Cycle Assessment (LCA) • ESG Frameworks (BRSR, SGX, EcoVadis)</p>
        </div>

        {/* Module Cards - 2x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* SSuM Card */}
          {isModuleEnabled('ssum') && (
            <div className="bg-gradient-to-br from-purple-50 to-indigo-100 border-2 border-purple-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Target className="w-10 h-10 text-purple-600" />
                  <h2 className="text-xl font-bold text-purple-900">SSuM Maturity</h2>
                </div>
                <p className="text-purple-700 mb-4 text-sm">
                  Strategic Sustainability Maturity Assessment. Board-level advisory with 5-dimension 
                  scoring (Level 1-6), gap analysis, and AI-powered transformation roadmap.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-purple-800">
                  <span className="px-2 py-1 bg-purple-200 rounded">5 Dimensions</span>
                  <span className="px-2 py-1 bg-purple-200 rounded">Gap Analysis</span>
                  <span className="px-2 py-1 bg-purple-200 rounded">AI Roadmap</span>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* CBAM Engine Card */}
          {isModuleEnabled('cbam') && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Leaf className="w-10 h-10 text-green-600" />
                  <h2 className="text-xl font-bold text-green-900">CBAM Engine</h2>
                </div>
                <p className="text-green-700 mb-4 text-sm">
                  Product-Wise Carbon Engine for EU CBAM compliance. Calculate embedded carbon emissions 
                  per product (tCO₂e/MT) and generate shipment-level CBAM declarations.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-green-800">
                  <span className="px-2 py-1 bg-green-200 rounded">3 Allocation Methods</span>
                  <span className="px-2 py-1 bg-green-200 rounded">Audit Log</span>
                  <span className="px-2 py-1 bg-green-200 rounded">PDF Export</span>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* GRI/CSRD Card */}
          {isModuleEnabled('gri') && (
            <div className="bg-gradient-to-br from-indigo-50 to-blue-100 border-2 border-indigo-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="w-10 h-10 text-indigo-600" />
                  <h2 className="text-xl font-bold text-indigo-900">GRI / CSRD</h2>
                </div>
                <p className="text-indigo-700 mb-4 text-sm">
                  Global Reporting Initiative (GRI) and EU Corporate Sustainability Reporting Directive (CSRD). 
                  Double materiality assessment and comprehensive ESG disclosure.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-indigo-800">
                  <span className="px-2 py-1 bg-indigo-200 rounded">Double Materiality</span>
                  <span className="px-2 py-1 bg-indigo-200 rounded">20 ESG Topics</span>
                  <span className="px-2 py-1 bg-indigo-200 rounded">Matrix PDF</span>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* LCA Card */}
          {isModuleEnabled('lca') && (
            <div className="bg-gradient-to-br from-teal-50 to-cyan-100 border-2 border-teal-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Recycle className="w-10 h-10 text-teal-600" />
                  <h2 className="text-xl font-bold text-teal-900">LCA Engine</h2>
                </div>
                <p className="text-teal-700 mb-4 text-sm">
                  ISO 14040/14044 compliant Life Cycle Assessment. 4-phase strict methodology with
                  version control, phase locking, and immutable audit trail.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-teal-800">
                  <span className="px-2 py-1 bg-teal-200 rounded">4 ISO Phases</span>
                  <span className="px-2 py-1 bg-teal-200 rounded">Version Control</span>
                  <span className="px-2 py-1 bg-teal-200 rounded">ERS/CBAM Auto</span>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* BRSR Card */}
          {isModuleEnabled('brsr') && (
            <div className="bg-gradient-to-br from-blue-50 to-sky-100 border-2 border-blue-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className="w-10 h-10 text-blue-600" />
                  <h2 className="text-xl font-bold text-blue-900">BRSR India</h2>
                </div>
                <p className="text-blue-700 mb-4 text-sm">
                  India SEBI Business Responsibility & Sustainability Reporting. 9 principles covering
                  ethics, product sustainability, employee well-being, and stakeholder engagement.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-blue-800">
                  <span className="px-2 py-1 bg-blue-200 rounded">9 Principles</span>
                  <span className="px-2 py-1 bg-blue-200 rounded">45 Indicators</span>
                  <span className="px-2 py-1 bg-blue-200 rounded">AI Narratives</span>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* SGX Card */}
          {isModuleEnabled('sgx') && (
            <div className="bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-emerald-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-10 h-10 text-emerald-600" />
                  <h2 className="text-xl font-bold text-emerald-900">SGX Singapore</h2>
                </div>
                <p className="text-emerald-700 mb-4 text-sm">
                  Singapore Exchange Sustainability Reporting. 5 ESG categories with 27+ core and
                  advanced metrics, TCFD-aligned climate disclosures.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-emerald-800">
                  <span className="px-2 py-1 bg-emerald-200 rounded">5 ESG Categories</span>
                  <span className="px-2 py-1 bg-emerald-200 rounded">TCFD Aligned</span>
                  <span className="px-2 py-1 bg-emerald-200 rounded">Core/Advanced</span>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* EcoVadis Card */}
          {isModuleEnabled('ecovadis') && (
            <div className="bg-gradient-to-br from-green-50 to-lime-100 border-2 border-green-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Award className="w-10 h-10 text-green-700" />
                  <h2 className="text-xl font-bold text-green-900">EcoVadis</h2>
                </div>
                <p className="text-green-700 mb-4 text-sm">
                  Global ESG Assessment Platform. 4 themes with question-based assessment, automatic
                  scoring (0-100), and medal calculation (Bronze/Silver/Gold/Platinum).
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-green-800">
                  <span className="px-2 py-1 bg-green-200 rounded">4 Themes</span>
                  <span className="px-2 py-1 bg-green-200 rounded">Auto Scoring</span>
                  <span className="px-2 py-1 bg-green-200 rounded">Medal System</span>
                </div>
              </div>
            </div>
            </div>
          )}
        </div>

        {/* Studies Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Select a Study for Sustainability Analysis</h3>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="text-gray-600 mt-4">Loading studies...</p>
            </div>
          ) : studies.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Factory className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">No studies found</p>
              <button
                onClick={() => navigate('/energy-resources')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Create ERS Study
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {studies.map((study) => (
                <div
                  key={study.id}
                  className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Factory className="w-5 h-5 text-gray-600" />
                      <h4 className="font-semibold text-gray-900">{study.study_name}</h4>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      study.status === 'Ongoing' ? 'bg-green-100 text-green-700' :
                      study.status === 'Draft' ? 'bg-gray-100 text-gray-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {study.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{study.site_name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{study.baseline_start} to {study.baseline_end}</span>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200 grid grid-cols-4 gap-2">
                    {isModuleEnabled('ssum') && (
                      <button
                        onClick={() => { setSelectedStudy(study); setActiveModule('ssum'); }}
                        className="text-purple-600 hover:bg-purple-600 hover:text-white font-medium text-xs flex flex-col items-center gap-1 bg-purple-50 px-2 py-3 rounded-lg border-2 border-purple-200 hover:border-purple-600 transition-all"
                      >
                        <Target className="w-5 h-5" />
                        <span>SSuM</span>
                      </button>
                    )}
                    {isModuleEnabled('cbam') && (
                      <button
                        onClick={() => { setSelectedStudy(study); setActiveModule('cbam'); }}
                        className="text-green-600 hover:bg-green-600 hover:text-white font-medium text-xs flex flex-col items-center gap-1 bg-green-50 px-2 py-3 rounded-lg border-2 border-green-200 hover:border-green-600 transition-all"
                      >
                        <Leaf className="w-5 h-5" />
                        <span>CBAM</span>
                      </button>
                    )}
                    {isModuleEnabled('gri') && (
                      <button
                        onClick={() => { setSelectedStudy(study); setActiveModule('gri'); }}
                        className="text-indigo-600 hover:bg-indigo-600 hover:text-white font-medium text-xs flex flex-col items-center gap-1 bg-indigo-50 px-2 py-3 rounded-lg border-2 border-indigo-200 hover:border-indigo-600 transition-all"
                      >
                        <Globe className="w-5 h-5" />
                        <span>GRI</span>
                      </button>
                    )}
                    {isModuleEnabled('lca') && (
                      <button
                        onClick={() => { setSelectedStudy(study); setActiveModule('lca'); }}
                        className="text-teal-600 hover:bg-teal-600 hover:text-white font-medium text-xs flex flex-col items-center gap-1 bg-teal-50 px-2 py-3 rounded-lg border-2 border-teal-200 hover:border-teal-600 transition-all"
                      >
                        <Recycle className="w-5 h-5" />
                        <span>LCA</span>
                      </button>
                    )}
                    {isModuleEnabled('brsr') && (
                      <button
                        onClick={() => { setSelectedStudy(study); setActiveModule('brsr'); }}
                        className="text-blue-600 hover:bg-blue-600 hover:text-white font-medium text-xs flex flex-col items-center gap-1 bg-blue-50 px-2 py-3 rounded-lg border-2 border-blue-200 hover:border-blue-600 transition-all"
                      >
                        <Building2 className="w-5 h-5" />
                        <span>BRSR</span>
                      </button>
                    )}
                    {isModuleEnabled('sgx') && (
                      <button
                        onClick={() => { setSelectedStudy(study); setActiveModule('sgx'); }}
                        className="text-emerald-600 hover:bg-emerald-600 hover:text-white font-medium text-xs flex flex-col items-center gap-1 bg-emerald-50 px-2 py-3 rounded-lg border-2 border-emerald-200 hover:border-emerald-600 transition-all"
                      >
                        <Shield className="w-5 h-5" />
                        <span>SGX</span>
                      </button>
                    )}
                    {isModuleEnabled('ecovadis') && (
                      <button
                        onClick={() => { setSelectedStudy(study); setActiveModule('ecovadis'); }}
                        className="text-green-700 hover:bg-green-700 hover:text-white font-medium text-xs flex flex-col items-center gap-1 bg-green-50 px-2 py-3 rounded-lg border-2 border-green-200 hover:border-green-700 transition-all"
                      >
                        <Award className="w-5 h-5" />
                        <span>EcoVadis</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Future Modules Preview */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Additional Modules (Coming Soon)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6 opacity-60">
              <div className="text-2xl font-bold text-blue-700 mb-2">♻️</div>
              <h3 className="text-lg font-bold text-blue-900 mb-1">Circular Economy</h3>
              <p className="text-sm text-blue-600">Track material flows and recycling rates</p>
              <span className="text-xs text-blue-700 mt-2 inline-block">Coming Soon</span>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-lg p-6 opacity-60">
              <div className="text-2xl font-bold text-purple-700 mb-2">💧</div>
              <h3 className="text-lg font-bold text-purple-900 mb-1">Water Footprint</h3>
              <p className="text-sm text-purple-600">Monitor and optimize water usage</p>
              <span className="text-xs text-purple-700 mt-2 inline-block">Coming Soon</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SustainabilityHub;
